<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- Sticky top bar -->
      <div class="sticky top-0 z-20 bg-slate-50 dark:bg-slate-950 px-4 pt-4 pb-0">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h1 class="text-xl font-bold text-slate-800 dark:text-white leading-tight">Products</h1>
            <p class="text-xs text-slate-400 mt-0.5">{{ periodLabel }}</p>
          </div>
          <button @click="showFilters = !showFilters"
            class="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition"
            :class="showFilters
              ? 'bg-indigo-500 text-white'
              : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"/>
            </svg>
            Filters
          </button>
        </div>

        <div v-if="showFilters"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Period</label>
              <select v-model="filters.preset"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Sort By</label>
              <select v-model="filters.sort"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="revenue">Revenue</option>
                <option value="profit">Profit</option>
                <option value="sold">Qty Sold</option>
                <option value="margin">Margin %</option>
                <option value="stock">Stock</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-1 px-4 pb-6 pt-3 space-y-4">

        <!-- KPI grid -->
        <div class="grid grid-cols-3 lg:grid-cols-6 gap-3">
          <div v-for="kpi in productKPIs" :key="kpi.label"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 lg:p-4">
            <p class="text-[9px] lg:text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
            <p class="text-sm lg:text-base font-bold leading-tight" :class="kpi.color">{{ kpi.value }}</p>
            <p v-if="kpi.sub" class="text-[9px] lg:text-[10px] text-slate-400 mt-1">{{ kpi.sub }}</p>
          </div>
        </div>

        <!-- Row: Revenue vs Cost chart + Stock health -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Revenue vs Cost grouped bar -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Revenue vs Cost — Top 6</p>
            <Bar :data="revenueCostData" :options="groupedBarOptions" style="max-height:200px" />
          </div>

          <!-- Stock health doughnut -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Stock Health</p>
            <div class="flex items-center gap-6">
              <div class="shrink-0">
                <Doughnut :data="stockHealthData" :options="doughnutOptions" style="width:130px;height:130px" />
              </div>
              <div class="flex-1 space-y-2.5">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Healthy (10+)</span>
                  </div>
                  <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400">16 products</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Low (1–10)</span>
                  </div>
                  <span class="text-xs font-bold text-amber-500">3 products</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Out of Stock</span>
                  </div>
                  <span class="text-xs font-bold text-red-500 dark:text-red-400">2 products</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-slate-300 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Never Sold</span>
                  </div>
                  <span class="text-xs font-bold text-slate-500 dark:text-slate-400">3 products</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Top by Profit + Top by Qty Sold -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Top 5 by profit -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 by Profit</p>
              <span class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full">Most profitable</span>
            </div>
            <div class="space-y-3">
              <div v-for="p in topByProfit" :key="p.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-xl bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center text-[10px] font-bold text-emerald-600 dark:text-emerald-400 shrink-0">
                  {{ p.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ p.name }}</span>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(p.profit) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" :style="{ width: p.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ p.margin }}% margin · {{ p.sold }} units sold</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Top 5 by qty sold -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 by Qty Sold</p>
              <span class="text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full">Best sellers</span>
            </div>
            <div class="space-y-3">
              <div v-for="p in topByQty" :key="p.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-xl bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-[10px] font-bold text-indigo-600 dark:text-indigo-400 shrink-0">
                  {{ p.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ p.name }}</span>
                    <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400 ml-2 shrink-0">{{ p.sold }} units</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-indigo-500 rounded-full" :style="{ width: p.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ shortNum(p.revenue) }} revenue · {{ shortNum(p.avg) }} avg rate</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Worst margin + Dead stock -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Lowest 5 by margin -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Lowest 5 by Margin</p>
              <span class="text-[10px] font-semibold bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full">Needs attention</span>
            </div>
            <div class="space-y-3">
              <div v-for="p in lowestByMargin" :key="p.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-xl bg-amber-100 dark:bg-amber-900 flex items-center justify-center text-[10px] font-bold text-amber-600 dark:text-amber-400 shrink-0">
                  {{ p.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ p.name }}</span>
                    <span class="text-xs font-bold ml-2 shrink-0" :class="p.margin < 0 ? 'text-red-500 dark:text-red-400' : 'text-amber-500'">{{ p.margin }}%</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full rounded-full" :class="p.margin < 0 ? 'bg-red-500' : 'bg-amber-400'" :style="{ width: p.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ shortNum(p.profit) }} profit · {{ shortNum(p.revenue) }} sales</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Dead stock / never sold -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Dead Stock</p>
              <span class="text-[10px] font-semibold bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400 px-2 py-0.5 rounded-full">Zero sales this period</span>
            </div>
            <div class="space-y-2">
              <div v-for="p in deadStock" :key="p.name"
                class="flex items-center gap-3 bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                <div class="w-8 h-8 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-500 dark:text-slate-400 shrink-0">
                  {{ p.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{{ p.name }}</p>
                  <p class="text-[10px] text-slate-400">Stock: {{ p.stock }} units</p>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-xs font-bold text-slate-600 dark:text-slate-400">{{ shortNum(p.stockValue) }}</p>
                  <p class="text-[10px] text-slate-400">tied up</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- All products -->
        <div>
          <!-- Filter pills -->
          <div class="flex gap-2 mb-3">
            <button @click="productFilter = 'all'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="productFilter === 'all' ? 'bg-indigo-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              All Products
            </button>
            <button @click="productFilter = 'low'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="productFilter === 'low' ? 'bg-red-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              Low / No Stock
            </button>
            <button @click="productFilter = 'dead'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="productFilter === 'dead' ? 'bg-slate-600 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              Dead Stock
            </button>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div v-for="p in filteredProductTable" :key="p.name"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <!-- Header -->
              <div class="flex items-start justify-between mb-3">
                <p class="font-semibold text-slate-800 dark:text-white text-sm leading-snug max-w-[65%]">{{ p.name }}</p>
                <span class="text-xs font-semibold px-2.5 py-1 rounded-xl shrink-0 ml-2"
                  :class="p.stock > 10 ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                    : p.stock > 0   ? 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'
                    :                  'bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400'">
                  Stock: {{ p.stock }}
                </span>
              </div>
              <!-- Stats grid -->
              <div class="grid grid-cols-5 gap-1.5 text-center">
                <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Sold</p>
                  <p class="text-[11px] font-bold text-slate-700 dark:text-slate-300">{{ p.sold }}</p>
                </div>
                <div class="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Revenue</p>
                  <p class="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(p.revenue) }}</p>
                </div>
                <div class="bg-violet-50 dark:bg-violet-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Profit</p>
                  <p class="text-[11px] font-bold text-violet-600 dark:text-violet-400">{{ shortNum(p.profit) }}</p>
                </div>
                <div class="bg-indigo-50 dark:bg-indigo-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Stk Val</p>
                  <p class="text-[11px] font-bold text-indigo-600 dark:text-indigo-400">{{ shortNum(p.stockValue) }}</p>
                </div>
                <div class="rounded-xl p-2" :class="p.margin >= 25 ? 'bg-emerald-50 dark:bg-emerald-950' : p.margin >= 10 ? 'bg-amber-50 dark:bg-amber-950' : 'bg-red-50 dark:bg-red-950'">
                  <p class="text-[9px] text-slate-400 mb-0.5">Margin</p>
                  <p class="text-[11px] font-bold" :class="p.margin >= 25 ? 'text-emerald-600 dark:text-emerald-400' : p.margin >= 10 ? 'text-amber-500' : 'text-red-500 dark:text-red-400'">{{ p.margin }}%</p>
                </div>
              </div>
              <!-- Footer -->
              <div class="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-50 dark:border-slate-800">
                <p class="text-[10px] text-slate-400">Avg rate: <span class="font-semibold text-slate-600 dark:text-slate-300">{{ shortNum(p.avgRate) }}</span></p>
                <p class="text-[10px] text-slate-400">Purchased: <span class="font-semibold text-slate-600 dark:text-slate-300">{{ p.purchased }} units</span></p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Bar, Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => { document.title = `Products Report | ${businessName.value}` })

const showFilters  = ref(false)
const productFilter = ref('all')
const filters = ref({ preset: 'this_year', sort: 'revenue' })

const periodLabel = computed(() => {
  const map = { this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year' }
  return map[filters.value.preset] || 'This Year'
})

function shortNum(n) {
  n = Number(n) || 0
  if (n >= 100000) return 'Rs ' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)   return 'Rs ' + (n / 1000).toFixed(1) + 'k'
  return 'Rs ' + n
}

const tickColor = '#94a3b8'
const gridColor = 'rgba(148,163,184,0.12)'
const baseFont  = { family: 'inherit', size: 10 }

// ── KPIs ─────────────────────────────────────────────
const productKPIs = [
  { label: 'Products Sold',    value: '24',        color: 'text-slate-800 dark:text-white',          sub: 'unique products' },
  { label: 'Total Revenue',    value: 'Rs 3.76L',  color: 'text-emerald-600 dark:text-emerald-400',  sub: 'from sales' },
  { label: 'Total Profit',     value: 'Rs 98.4k',  color: 'text-violet-600 dark:text-violet-400',    sub: '26% avg margin' },
  { label: 'Stock Val (Cost)', value: 'Rs 8.92L',  color: 'text-indigo-600 dark:text-indigo-400',    sub: 'at purchase price' },
  { label: 'Stock Val (Sale)', value: 'Rs 11.4L',  color: 'text-slate-800 dark:text-white',          sub: 'at sale rate' },
  { label: 'Low / Zero Stock', value: '5',          color: 'text-red-500 dark:text-red-400',          sub: 'need reorder' },
]

// ── Revenue vs Cost grouped bar ───────────────────────
const groupedBarOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { labels: { color: tickColor, font: baseFont, boxWidth: 10, padding: 12 } }, tooltip: { mode: 'index' } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, callback: v => (v / 1000) + 'k' } },
  },
}
const revenueCostData = {
  labels: ['Cement', 'Steel', 'GI Sheet', 'PVC Pipe', 'Wire', 'Paint'],
  datasets: [
    { label: 'Revenue', data: [84000, 72000, 57000, 46500, 35000, 30000], backgroundColor: 'rgba(16,185,129,0.85)', borderRadius: 4 },
    { label: 'Cost',    data: [58800, 50400, 42180, 32550, 28000, 22500], backgroundColor: 'rgba(99,102,241,0.85)',  borderRadius: 4 },
  ],
}

// ── Stock health doughnut ─────────────────────────────
const doughnutOptions = {
  responsive: false,
  plugins: { legend: { display: false } },
  cutout: '70%',
}
const stockHealthData = {
  labels: ['Healthy', 'Low', 'Out of Stock', 'Never Sold'],
  datasets: [{ data: [16, 3, 2, 3], backgroundColor: ['rgba(16,185,129,0.85)', 'rgba(251,191,36,0.85)', 'rgba(239,68,68,0.85)', 'rgba(148,163,184,0.6)'], borderWidth: 0 }],
}

// ── Top by profit ─────────────────────────────────────
const topByProfit = [
  { name: 'Cement 50kg (OPC)', profit: 25200, margin: 30, sold: 420, pct: 100 },
  { name: 'Steel Rod 12mm',    profit: 21600, margin: 30, sold: 180, pct: 86  },
  { name: 'GI Sheet 20g',      profit: 14820, margin: 26, sold: 95,  pct: 59  },
  { name: 'PVC Pipe 1"',       profit: 13950, margin: 30, sold: 310, pct: 55  },
  { name: 'Wire 2.5mm',        profit: 7000,  margin: 20, sold: 500, pct: 28  },
]

// ── Top by qty sold ───────────────────────────────────
const topByQty = [
  { name: 'Wire 2.5mm',        sold: 500, revenue: 35000, avg: 70,   pct: 100 },
  { name: 'Cement 50kg (OPC)', sold: 420, revenue: 84000, avg: 200,  pct: 84  },
  { name: 'PVC Pipe 1"',       sold: 310, revenue: 46500, avg: 150,  pct: 62  },
  { name: 'Brick (Standard)',  sold: 200, revenue: 24000, avg: 120,  pct: 40  },
  { name: 'Steel Rod 12mm',    sold: 180, revenue: 72000, avg: 400,  pct: 36  },
]

// ── Lowest by margin ──────────────────────────────────
const lowestByMargin = [
  { name: 'Brick (Standard)',  margin: -5,  profit: -1200, revenue: 24000, pct: 100 },
  { name: 'GI Pipe 1"',        margin: 6,   profit: 1680,  revenue: 28000, pct: 73  },
  { name: 'Emulsion Paint 4L', margin: 10,  profit: 3000,  revenue: 30000, pct: 55  },
  { name: 'Wire 2.5mm',        margin: 20,  profit: 7000,  revenue: 35000, pct: 37  },
  { name: 'GI Sheet 20g',      margin: 26,  profit: 14820, revenue: 57000, pct: 22  },
]

// ── Dead stock ────────────────────────────────────────
const deadStock = [
  { name: 'Angle Iron 2"',       stock: 45, stockValue: 31500 },
  { name: 'HDPE Pipe 2"',        stock: 28, stockValue: 19600 },
  { name: 'Roof Sheet 10ft',     stock: 12, stockValue: 16800 },
]

// ── All products table ────────────────────────────────
const productTableData = [
  { name: 'Cement 50kg (OPC)',  stock: 85,  sold: 420, purchased: 500, revenue: 84000, profit: 25200, margin: 30, stockValue: 55250, avgRate: 200  },
  { name: 'Steel Rod 12mm',     stock: 42,  sold: 180, purchased: 210, revenue: 72000, profit: 21600, margin: 30, stockValue: 29400, avgRate: 400  },
  { name: 'GI Sheet 20 gauge',  stock: 18,  sold: 95,  purchased: 110, revenue: 57000, profit: 14820, margin: 26, stockValue: 12600, avgRate: 600  },
  { name: 'PVC Pipe 1 inch',    stock: 120, sold: 310, purchased: 420, revenue: 46500, profit: 13950, margin: 30, stockValue: 10800, avgRate: 150  },
  { name: 'Wire 2.5mm',         stock: 8,   sold: 500, purchased: 505, revenue: 35000, profit: 7000,  margin: 20, stockValue: 5600,  avgRate: 70   },
  { name: 'Emulsion Paint 4L',  stock: 3,   sold: 75,  purchased: 77,  revenue: 30000, profit: 3000,  margin: 10, stockValue: 2250,  avgRate: 400  },
  { name: 'GI Pipe 1 inch',     stock: 0,   sold: 60,  purchased: 60,  revenue: 28000, profit: 1680,  margin: 6,  stockValue: 0,     avgRate: 467  },
  { name: 'Brick (Standard)',   stock: 0,   sold: 200, purchased: 200, revenue: 24000, profit: -1200, margin: -5, stockValue: 0,     avgRate: 120  },
  { name: 'Angle Iron 2"',      stock: 45,  sold: 0,   purchased: 45,  revenue: 0,     profit: 0,     margin: 0,  stockValue: 31500, avgRate: 0    },
  { name: 'HDPE Pipe 2"',       stock: 28,  sold: 0,   purchased: 28,  revenue: 0,     profit: 0,     margin: 0,  stockValue: 19600, avgRate: 0    },
]

const filteredProductTable = computed(() => {
  if (productFilter.value === 'low')  return productTableData.filter(p => p.stock <= 5)
  if (productFilter.value === 'dead') return productTableData.filter(p => p.sold === 0)
  return productTableData
})
</script>

<style scoped>
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>
